import type { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from "axios";
import router from "@/router";
import store from "@/store";
import { LOGIN_ROUTE } from "@/router/routes";
import { LOGOUT, UPDATE_ERRORMSG } from "@/store/action-types";
import { SERVER_ERR_MSG } from "@/utils/const";
import { classifyError, extractAccountIdFromRequest } from "@/utils/errorClassifier";
import { getRefreshedAccessToken, isRefreshEndpoint, retryWithToken } from "@/api/tokenRefresh";
import { LOGOUT_URL } from "@/utils/urls";

interface RetryableRequestConfig extends InternalAxiosRequestConfig {
  _retriedAfterRefresh?: boolean;
  _sessionLogoutDispatched?: boolean;
}

/**
 * Handles classified errors (session, provider account, rate limit, timeout)
 */
export async function handleClassifiedError(
  error: AxiosError
): Promise<{ handled: boolean; retriedResponse?: AxiosResponse }> {
  const responseData = error.response?.data as { message?: string } | undefined;
  const backendMessage = responseData?.message;
  const classification = classifyError(error);

  // Handle user session errors
  if (classification.type === "USER_SESSION" && classification.shouldLogout) {
    const config = error.config as RetryableRequestConfig | undefined;

    // The revoke call the LOGOUT action itself fires (Auth.logout) can come
    // back session-classified when the refresh token being revoked is
    // already invalid. That call is already inside a LOGOUT that's still
    // running and will clear local state and swallow this rejection on its
    // own — attempting a refresh or dispatching another LOGOUT here would
    // just re-enter that same pipeline while the outer call awaits it.
    if (config?.url?.includes(LOGOUT_URL)) {
      return { handled: true };
    }

    const canAttemptRefresh =
      !!config &&
      !config._retriedAfterRefresh &&
      !isRefreshEndpoint(config.url) &&
      !!store.getters.refreshToken;

    if (canAttemptRefresh) {
      const refreshResult = await getRefreshedAccessToken();
      if (refreshResult.token && config) {
        config._retriedAfterRefresh = true;
        try {
          const retriedResponse = await retryWithToken(config, refreshResult.token);
          return { handled: true, retriedResponse };
        } catch {
          // If the retried request itself came back session-expired, the
          // nested interceptor invocation (triggered by baseAPI's own
          // response interceptor inside retryWithToken) already dispatched
          // LOGOUT for this same config — don't do it again below.
          if (config._sessionLogoutDispatched) {
            return { handled: true };
          }
          // Any other failure on the retried request (network blip,
          // unrelated 4xx/5xx) doesn't mean the refreshed session is
          // invalid — the refresh above succeeded. Don't fall through to
          // the unconditional logout below; let the failure propagate
          // through the normal error pipeline instead.
          return { handled: false };
        }
      } else if (refreshResult.networkError) {
        // Couldn't reach the refresh endpoint at all (offline/timeout) —
        // don't treat a connectivity blip as an invalid session. Let this
        // request fail on its own; the next request gets another chance.
        return { handled: true };
      }
    }

    const alreadyOnLogin = router.currentRoute.value.name === LOGIN_ROUTE;
    if (!alreadyOnLogin) {
      store.dispatch(UPDATE_ERRORMSG, {
        errorMsg: classification.message || backendMessage || SERVER_ERR_MSG,
      });
    }
    if (config) config._sessionLogoutDispatched = true;
    await store.dispatch(LOGOUT);
    router.push({ name: LOGIN_ROUTE });
    return { handled: true };
  }

  // Handle provider account errors
  if (classification.type === "PROVIDER_ACCOUNT") {
    const accountId = extractAccountIdFromRequest(error);
    if (accountId) {
      store.dispatch("markUnipileReconnectRequired", {
        channelId: accountId,
        required: true,
      });
    }
    return { handled: true };
  }

  // Handle timeout errors (already classified)
  if (classification.type === "TIMEOUT") {
    return { handled: true };
  }

  // Handle rate limit errors
  if (classification.type === "RATE_LIMIT") {
    store.dispatch(UPDATE_ERRORMSG, {
      errorMsg: classification.message || "Too many requests. Please wait a moment.",
    });
    return { handled: true };
  }

  return { handled: false };
}
